import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { collect } from 'collect.js';
import { Select, Tabs } from 'antd';
import PerformanceTab from './PerformanceTab';
import NewRestaurantsTab from './NewRestaurantsTab';
import CustomerHealthTab from './CustomerHealthTab';
import LiveAndInactiveTab from './LiveAndInactiveTab';
import useApiClient from '../../hooks/api/useApiClient';
import usePlgOnboarding from '../../hooks/auth/usePlgOnboarding';
import usePermissions, { ManagerPermission, ManagerRole } from '../../hooks/auth/usePermissions';
import BillingHealthTab from './BillingHealthTab';

export const Tab = {
  Performance: 'performance',
  NewCustomers: 'new_customers',
  CustomerHealth: 'customer_health',
  LiveAndInactive: 'live_and_inactive',
  BillingHealth: 'billing_health',
};

export default function CustomerBaseView() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:CustomerBase' });

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const { profile } = useSelector((state) => state.initialData);

  const { ManagersManager } = useApiClient();

  const { can } = usePermissions();

  const isPlgPartner = usePlgOnboarding();

  const [activeTab, setActiveTab] = useState(Tab.Performance);
  const [managers, setManagers] = useState([]);
  const [selectedManagers, setSelectedManagers] = useState(
    can(ManagerPermission.GlobalReadCustomerBaseStats) ? [] : [profile.id],
  );

  const managerOptions = useMemo(
    () => managers.map((item) => ({ value: item.id, label: item.name ? item.name : item.email })),
    [managers],
  );

  useEffect(() => {
    can(ManagerPermission.GlobalReadCustomerBaseStats) && loadManagers();
  }, [serviceProvider?.id]);

  const loadManagers = async () => {
    const { success, data } = await ManagersManager.get({ per_page: 100 });

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    const isSalesLeadership = profile?.roles?.includes(ManagerRole.SalesLeadership);

    const list = Array.isArray(data?.data) ? data.data : (data ?? []);
    const managers = collect(list)
      .filter((member) =>
        [
          ...(isSalesLeadership ? [ManagerRole.Reseller] : []),
          ManagerRole.SalesManager,
          ManagerRole.OnboardingManager,
          ManagerRole.SalesLeadership,
        ].some((role) => member.roles.includes(role)),
      )
      .push(profile);

    setManagers(managers.toArray());

    isSalesLeadership &&
      setSelectedManagers(
        managers
          .filter((manager) => manager.roles.includes(ManagerRole.Reseller))
          .map((manager) => manager.id)
          .toArray(),
      );
  };

  const _tabs = [
    {
      id: Tab.Performance,
      has_access: true,
    },
    {
      id: Tab.NewCustomers,
      has_access: true,
    },
    {
      // Hidden from PLG partners (Artur's round-3 feedback §5): Customer Health
      // and Billing Health are FoodAmigos-internal account management, not
      // something to put in a partner's hands. The rest of the view is the
      // portfolio overview they asked to keep.
      id: Tab.CustomerHealth,
      has_access: !isPlgPartner,
    },
    {
      id: Tab.LiveAndInactive,
      has_access: true,
    },
    {
      id: Tab.BillingHealth,
      has_access: !isPlgPartner && can(ManagerPermission.ReadBillingHealthStats),
    },
  ];

  function ManagerSelect() {
    return (
      can(ManagerPermission.GlobalReadCustomerBaseStats) && (
        <Select
          allowClear
          mode="multiple"
          showSearch={{ optionFilterProp: 'label' }}
          value={selectedManagers}
          options={managerOptions}
          onChange={setSelectedManagers}
          className="w-64 mt-md"
          placeholder={t('labels.sales_manager')}
        />
      )
    );
  }

  function TabContent() {
    const props = { selectedManagers, renderManagerSelect: () => <ManagerSelect /> };

    return (
      {
        [Tab.Performance]: <PerformanceTab {...props} />,
        [Tab.NewCustomers]: <NewRestaurantsTab {...props} />,
        [Tab.CustomerHealth]: <CustomerHealthTab {...props} />,
        [Tab.LiveAndInactive]: <LiveAndInactiveTab {...props} />,
        [Tab.BillingHealth]: <BillingHealthTab {...props} />,
      }[activeTab] ?? null
    );
  }

  return (
    <>
      <Tabs
        activeKey={activeTab}
        items={_tabs
          .filter((item) => item.has_access)
          .map((item) => ({
            key: item.id,
            label: t(`tabs.${item.id}`),
          }))}
        onChange={(tab) => setActiveTab(tab)}
      />
      <div className="mt-md">
        <TabContent />
      </div>
    </>
  );
}
